// PublicLandingPage.jsx - Version simplifiée sans étape intermédiaire d'inscription
import React, { useState, useEffect, useRef } from 'react';
import { 
  BookOpen, Lightbulb, MessageCircle, Share2, Star,
  ChevronLeft, ChevronRight, Check, Sparkles,
  GraduationCap, TrendingUp, Users, Globe, Brain, Compass,
  ArrowRight, Play, CheckCircle, Award, Zap,
  Shield, Rocket, BarChart3, Heart, Handshake, TreePine,
  AlertCircle, Clock, Trophy, Building, BookOpenCheck, Monitor,
  ChevronDown
} from 'lucide-react';
import { useI18n } from '../CP-Multilingue/useI18n';

// Import des services
import { askGPT } from '../components/OpenAIService';
import demoLoginService from './DemoLoginService';

// Import des prompts multilingues
import { 
  getLocalizedPrompt, 
  getErrorMessages, 
  getFallbackResources 
} from './PublicPrompts';

const DemoModule = ({ onShowAuth }) => {
  const { t, isRTL, currentLanguage } = useI18n();
  const [demoStep, setDemoStep] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [generatedPreview, setGeneratedPreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Méthode pour nettoyer et rediriger vers l'authentification
  const handleCleanupAndAuth = async (authType, userData = {}) => {
    try {
      // Nettoyer la session démo avant l'authentification utilisateur
      await demoLoginService.ensureCleanStateForUserAuth();
      
      // Appeler la méthode d'authentification avec l'objectif de démo
      onShowAuth(authType, {
        ...userData,
        demoObjective: userData.demoObjective || userInput
      });
    } catch (error) {
      console.error('Erreur lors du nettoyage avant authentification:', error);
      // Continuer quand même avec l'authentification
      onShowAuth(authType, {
        ...userData,
        demoObjective: userData.demoObjective || userInput
      });
    }
  };

  // Gestion des événements de nettoyage de session
  useEffect(() => {
    const handleDemoSessionClosed = (event) => {
      console.log('Session démo fermée:', event.detail.message);
    };

    window.addEventListener('demoSessionClosed', handleDemoSessionClosed);

    // Nettoyage lors du démontage du composant
    return () => {
      window.removeEventListener('demoSessionClosed', handleDemoSessionClosed);
      // Nettoyer la session démo si le composant est démonté
      if (demoLoginService.isDemoSession()) {
        demoLoginService.cleanup().catch(console.warn);
      }
    };
  }, []);

  // Initialisation vide
  useEffect(() => {
    setUserInput('');
  }, []);
 
  const demoObjectives = [
    t('landing.demo.examples.0', 'Lancer ma startup tech innovante'),
    t('landing.demo.examples.1', 'Investir dans l\'immobilier locatif intelligent'),
    t('landing.demo.examples.2', 'Développer une marque personnelle'),
    t('landing.demo.examples.3', 'Développer mon leadership d\'équipe'),
    t('landing.demo.examples.4', 'Monter une startup à impact écologique'),
    t('landing.demo.examples.5', 'Acquérir une compétence rare et certifiante')
  ];

  // Icônes pour les ressources expertes
  const resourceIcons = [
    { icon: Building, color: 'text-blue-600' },      // Institution/Organisation
    { icon: BookOpenCheck, color: 'text-purple-600' }, // Formation/Cours
    { icon: Monitor, color: 'text-emerald-600' }      // Plateforme/Outil
  ];

  const generateDemoPreview = async () => {
    if (!userInput.trim()) return;
    setIsGenerating(true);
    setShowPreview(true);
    
    try {
      // 1. Se connecter au service pour la démo
      await demoLoginService.loginToDemo();
      
      const currentObjective = userInput.trim();
      const prompt = getLocalizedPrompt(currentObjective, currentLanguage);
      const gptResponse = await askGPT(prompt);
      
      // Nettoyage robuste de la réponse avant parsing
      let cleanedResponse = gptResponse;
      cleanedResponse = cleanedResponse.trim();
      cleanedResponse = cleanedResponse.replace(/^```json\n?/i, '').replace(/\n?```$/i, '');
      cleanedResponse = cleanedResponse.replace(/^```\n?/i, '').replace(/\n?```$/i, '');
      
      const jsonStart = cleanedResponse.indexOf('{');
      const jsonEnd = cleanedResponse.lastIndexOf('}');
      
      if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
        cleanedResponse = cleanedResponse.substring(jsonStart, jsonEnd + 1);
      }
      
      const errorMessages = getErrorMessages(currentLanguage);
      
      let parsedStrategy;
      try {
        parsedStrategy = JSON.parse(cleanedResponse);
      } catch (jsonError) {
        console.error('Erreur JSON détaillée:', {
          originalResponse: gptResponse,
          cleanedResponse: cleanedResponse,
          jsonError: jsonError.message,
          jsonStart,
          jsonEnd
        });
        throw new Error(errorMessages.invalidFormatDesc);
      }
      
      // Validation de la structure obligatoire
      if (!parsedStrategy.titre || !parsedStrategy.phases || !Array.isArray(parsedStrategy.phases)) {
        console.error('Structure invalide:', parsedStrategy);
        throw new Error(errorMessages.incompleteStructureDesc);
      }
      
      // Validation des phases (minimum requis)
      if (parsedStrategy.phases.length === 0) {
        throw new Error(errorMessages.noStepsDesc);
      }
      
      // Validation des ressources (s'assurer qu'elles existent)
      if (!parsedStrategy.ressources || !Array.isArray(parsedStrategy.ressources)) {
        const fallbackRes = getFallbackResources(currentLanguage);
        parsedStrategy.ressources = [
          `${fallbackRes.expert}: ${currentObjective}`,
          `${fallbackRes.training}: ${currentObjective}`,
          `${fallbackRes.platform}: ${currentObjective}`
        ];
      }

      // 2. Sauvegarder les données de démo
      await demoLoginService.saveDemoObjective({
        userInput: currentObjective,
        gptResponse: gptResponse,
        cleanedResponse: cleanedResponse,
        parsedStrategy: parsedStrategy,
        prompt: prompt,
        language: currentLanguage,
        source: 'public_landing_demo',
        success: true
      });

      // 3. Afficher le résultat
      setGeneratedPreview(parsedStrategy);
      setTimeout(() => setDemoStep(1), 500);
      
      // 4. DÉCONNEXION IMMÉDIATE après affichage (1 seconde pour l'animation)
      setTimeout(async () => {
        console.log('🧹 Déconnexion immédiate après affichage du résultat');
        await demoLoginService.cleanup();
      }, 1000);
      
    } catch (error) {
      console.error('Erreur generateDemoPreview:', error);
      
      // IMPORTANT: Se déconnecter même en cas d'erreur
      setTimeout(async () => {
        console.log('🧹 Déconnexion après erreur');
        await demoLoginService.cleanup();
      }, 500);
      
      // Gestion des erreurs avec messages localisés
      const errorMessages = getErrorMessages(currentLanguage);
      let userMessage = errorMessages.unexpected;
      
      if (error.message.includes(errorMessages.invalidFormat)) {
        userMessage = errorMessages.invalidFormatDesc;
      } else if (error.message.includes(errorMessages.incompleteStructure)) {
        userMessage = errorMessages.incompleteStructureDesc;
      } else if (error.message.includes(errorMessages.noSteps)) {
        userMessage = errorMessages.noStepsDesc;
      } else if (error.message) {
        userMessage = error.message;
      }
      
      setGeneratedPreview({
        error: true,
        titre: errorMessages.title,
        message: userMessage,
        canRetry: true
      });
      setTimeout(() => setDemoStep(1), 500);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuickDemo = (objective) => {
    // S'assurer que l'input utilisateur reflète le choix
    setUserInput(objective);
    // Réinitialiser l'état pour éviter l'affichage de l'ancienne stratégie
    setGeneratedPreview(null);
    setShowPreview(false);
    setDemoStep(0);
    
    // Délai plus court pour un meilleur UX
    setTimeout(() => {
      generateDemoPreview();
    }, 100);
  };

  const textAlign = isRTL ? 'text-right' : 'text-left';

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-slate-200/60 p-8 lg:p-10 max-w-4xl mx-auto backdrop-blur-sm">
      {/* En-tête avec urgence - couleur orange */}
      <div className="text-center mb-8">
        <div className="bg-gradient-to-r from-orange-100 to-orange-50 border border-orange-300/50 rounded-2xl p-4 mb-6 shadow-sm">
          <div className="flex items-center justify-center text-orange-700 text-sm font-semibold">
            <Zap className="w-5 h-5 mr-2 text-orange-600" />
            {t('landing.demo.freeTrialResults', 'Essai gratuit - Résultats en 30 secondes')}
          </div>
        </div>
        
        {/* Icône éclair orangé */}
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl mb-6 shadow-lg border border-orange-200/50">
          <Zap className="w-10 h-10 text-orange-600" />
        </div>
        <h3 className="text-2xl lg:text-3xl font-bold mb-3">
          <span className="bg-gradient-to-r from-orange-500 to-purple-600 bg-clip-text text-transparent">
            {t('landing.demo.moduleTitle', 'Demander votre stratégie personnalisée')}
          </span>
        </h3>
        <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
          {t('landing.demo.moduleSubtitle', 'Décrivez votre projet et obtenez immédiatement votre plan d\'action sur-mesure')}
        </p>
      </div>

      {/* ÉTAPE 0 : Saisie */}
      {demoStep === 0 && (
        <div className="space-y-8">
          <div>
            <label className={`block text-lg font-semibold text-slate-700 mb-4 ${textAlign}`}>
              {t('landing.demo.inputLabel', 'Quel projet vous tient à cœur ?')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && generateDemoPreview()}
                placeholder={t('landing.demo.inputPlaceholder', 'Ex: Créer une école innovante, Développer une IA éthique...')}
                className={`w-full px-6 py-4 border-2 border-slate-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-all shadow-sm ${textAlign}`}
                disabled={isGenerating}
              />
              <button 
                onClick={generateDemoPreview}
                disabled={!userInput.trim() || isGenerating}
                className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl`}
              >
                {isGenerating ? (
                  <div className="animate-spin">
                    <Sparkles className="w-6 h-6" />
                  </div>
                ) : (
                  <Sparkles className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Exemples cliquables */}
          <div>
            <p className={`text-base font-medium text-slate-600 mb-4 ${textAlign}`}>
              {t('landing.demo.examplesTitle', 'Ou explorez ces exemples inspirants :')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {demoObjectives.slice(0, 4).map((objective, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickDemo(objective)}
                  disabled={isGenerating}
                  className={`${textAlign} p-4 border-2 border-gradient-to-r from-orange-300 to-purple-300 bg-gradient-to-r from-orange-50 via-white to-purple-50 rounded-xl hover:border-orange-400 hover:from-orange-100 hover:to-purple-100 transition-all text-base disabled:opacity-50 font-medium text-slate-700 hover:text-slate-800 shadow-md hover:shadow-lg transform hover:scale-[1.02] relative overflow-hidden`}
                  style={{
                    border: '2px solid transparent',
                    background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #fb923c, #a855f7) border-box'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-purple-500/5 rounded-xl"></div>
                  <span className="relative z-10">{objective}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Indicateurs de confiance */}
          <div className="flex items-center justify-center gap-8 text-base text-slate-600 bg-slate-50 rounded-2xl p-6">
            <div className="flex items-center font-medium">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              {t('landing.hero.socialProof.strategiesCreated', '2,847 stratégies créées ce mois')}
            </div>
            <div className="flex items-center font-medium">
              <Star className="w-5 h-5 text-yellow-500 mr-2" />
              {t('landing.hero.socialProof.averageRating', '4.8/5 étoiles')}
            </div>
          </div>
        </div>
      )}

      {/* Loading avec progression */}
      {showPreview && demoStep === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl mb-6 shadow-lg">
            <div className="animate-spin">
              <Sparkles className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h4 className="text-xl font-bold text-blue-600 mb-2">
            {t('landing.demo.generating', 'Analyse en cours...')}
          </h4>
          <p className="text-base text-slate-600 mb-6 max-w-md mx-auto">
            {t('landing.demo.generatingSubtext', 'Notre IA analyse votre objectif et prépare votre stratégie')}
          </p>
          <div className="w-64 bg-slate-200 rounded-full h-3 mx-auto">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-3 rounded-full animate-pulse transition-all duration-1000" style={{width: '70%'}}></div>
          </div>
        </div>
      )}

      {/* ÉTAPE 1 : Résultat avec inscription directe */}
      {demoStep === 1 && generatedPreview && (
        <div className="space-y-8">
          {generatedPreview.error ? (
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 border border-red-200 text-center shadow-lg">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-md">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h4 className="font-bold text-xl text-red-800 mb-3">{generatedPreview.titre}</h4>
              <p className="text-red-700 mb-6 text-base">{generatedPreview.message}</p>
              
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={() => generateDemoPreview()}
                  className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all font-medium shadow-lg hover:shadow-xl"
                >
                  {t('landing.demo.errorRetry', 'Réessayer')}
                </button>
                <button 
                  onClick={() => {
                    setDemoStep(0);
                    setShowPreview(false);
                    setGeneratedPreview(null);
                  }}
                  className="bg-slate-600 text-white px-8 py-3 rounded-xl hover:bg-slate-700 transition-all font-medium"
                >
                  {t('landing.demo.errorChangeObjective', 'Changer d\'objectif')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Résultats de la stratégie */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200/50 shadow-lg">
                <h4 className={`font-bold text-xl text-blue-800 mb-6 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Zap className="w-6 h-6 mx-3 text-orange-600" />
                  {generatedPreview.titre}
                </h4>
                
                <div className="space-y-6">
                  {/* Phases */}
                  <div>
                    <h5 className={`font-semibold text-lg text-blue-700 mb-4 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle className="w-5 h-5 mx-2 text-blue-600" />
                      {t('landing.demo.phases', 'Plan d\'action')}
                    </h5>
                    <ul className="space-y-4">
                      {generatedPreview.phases && generatedPreview.phases.map((phase, index) => (
                        <li key={index} className={`text-base text-blue-700 flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl flex items-center justify-center mx-3 text-sm font-bold mt-0.5 flex-shrink-0 shadow-md">
                            {index + 1}
                          </div>
                          <span className={`${textAlign} leading-relaxed font-medium`}>{phase}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Ressources expertes */}
                  {generatedPreview.ressources && generatedPreview.ressources.length > 0 && (
                    <div>
                      <h5 className={`font-semibold text-lg text-blue-700 mb-4 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Users className="w-5 h-5 mx-2 text-blue-600" />
                        {t('landing.demo.expertResources', 'Ressources expertes recommandées')}
                      </h5>
                      <ul className="space-y-3">
                        {generatedPreview.ressources.map((ressource, index) => {
                          const resourceIcon = resourceIcons[index] || resourceIcons[index % resourceIcons.length];
                          const IconComponent = resourceIcon.icon;
                          return (
                            <li key={index} className={`text-base text-blue-700 flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <IconComponent className={`w-5 h-5 mx-3 mt-0.5 flex-shrink-0 ${resourceIcon.color}`} />
                              <span className={`${textAlign} leading-relaxed font-medium`}>{ressource}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Stratégie */}
                  {generatedPreview.strategie && (
                    <div className="bg-white rounded-xl p-6 border border-blue-200/50 shadow-md">
                      <p className={`text-base text-blue-700 flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <MessageCircle className="w-6 h-6 mx-3 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span className={`${textAlign} leading-relaxed`}>
                          <strong className="font-semibold">{t('landing.demo.recommendedStrategy', 'Stratégie recommandée')} :</strong> {generatedPreview.strategie}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Inscription directe - VERSION SIMPLIFIÉE */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 border border-emerald-200/50 shadow-lg">
                <div className="text-center mb-6">
                  <Trophy className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                  <h4 className="font-bold text-xl text-emerald-800 mb-3">
                    {t('landing.demo.resultAccess', 'Accédez à votre stratégie complète')}
                  </h4>
                  <p className="text-emerald-700 text-base leading-relaxed">
                    {t('landing.demo.resultAccessSubtitle', 'Obtenez le plan détaillé, les ressources expertes et l\'accès à la communauté')}
                  </p>
                </div>

                {/* BOUTON DIRECT VERS L'INSCRIPTION */}
                <button
                  onClick={() => handleCleanupAndAuth('register', { demoObjective: userInput })}
                  className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-8 py-4 rounded-xl hover:from-emerald-700 hover:to-emerald-800 transition-all font-semibold text-lg flex items-center justify-center shadow-lg hover:shadow-xl"
                >
                  <Heart className="w-6 h-6 mr-3" />
                  {t('landing.demo.createCompleteAction', 'Créer mon plan d\'action complet')}
                </button>

                {/* TEXTE INFORMATIF */}
                <div className="flex items-center justify-center gap-6 mt-6 text-sm font-medium text-emerald-700">
                  <span className="flex items-center">
                    <Check className="w-4 h-4 mr-1" />
                    {t('signup.features.free30Days', 'Gratuit pendant 30 jours')}
                  </span>
                  <span className="flex items-center">
                    <Check className="w-4 h-4 mr-1" />
                    {t('signup.features.immediateAccess', 'Accès immédiat')}
                  </span>
                </div>
              </div>

              {/* Option alternative */}
              <div className="text-center">
                <button 
                  onClick={() => {
                    setDemoStep(0);
                    setShowPreview(false);
                    setGeneratedPreview(null);
                  }}
                  className="text-slate-600 hover:text-slate-700 font-semibold text-base flex items-center justify-center mx-auto bg-slate-50 hover:bg-slate-100 px-6 py-3 rounded-xl transition-all"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  {t('landing.demo.testAnother', 'Tester un autre objectif')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Composant principal
const PublicLandingPage = ({ onShowAuth, onDemoObjectiveCreated }) => {
  const { t, isRTL, currentLanguage } = useI18n();
  const demoSectionRef = useRef(null);

  // Méthode pour nettoyer et rediriger vers l'authentification
  const handleAuthWithCleanup = async (authType, userData = {}) => {
    try {
      await demoLoginService.ensureCleanStateForUserAuth();
      onShowAuth(authType, userData);
    } catch (error) {
      console.error('Erreur lors du nettoyage avant authentification:', error);
      onShowAuth(authType, userData);
    }
  };

  // Surveillance de session et nettoyage au montage
  useEffect(() => {
    // Nettoyer toute session démo existante au montage
    const cleanupExistingSession = async () => {
      if (demoLoginService.isDemoSession()) {
        console.log('Session démo détectée au chargement, nettoyage...');
        await demoLoginService.cleanup();
      }
    };

    cleanupExistingSession();

    // Event listener pour nettoyage de session
    const handleDemoSessionClosed = (event) => {
      console.log('Session démo fermée sur la landing page:', event.detail.message);
    };

    window.addEventListener('demoSessionClosed', handleDemoSessionClosed);

    return () => {
      window.removeEventListener('demoSessionClosed', handleDemoSessionClosed);
    };
  }, []);

  const scrollToDemo = () => {
    if (demoSectionRef.current) {
      demoSectionRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const stats = [
    { 
      number: t('landing.stats.sources.number', '∞'), 
      label: t('landing.stats.sources.label', 'Sources Illimitées'), 
      sublabel: t('landing.stats.sources.sublabel', 'Intelligence mondiale, zéro hallucination') 
    },
    { 
      number: t('landing.stats.projects.number', '💾'), 
      label: t('landing.stats.projects.label', 'Projets Sauvegardés'), 
      sublabel: t('landing.stats.projects.sublabel', 'Contexte préservé, reprise instantanée') 
    },
    { 
      number: t('landing.stats.transparency.number', '100%'), 
      label: t('landing.stats.transparency.label', 'Transparent'), 
      sublabel: t('landing.stats.transparency.sublabel', 'Algorithmes ouverts, sources citées') 
    }
  ];

  const textAlign = isRTL ? 'text-right' : 'text-left';

  return (
    <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.15'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
                
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-16 lg:pb-20">
          <div className="text-center">
            {/* Badge d'urgence */}
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-100 to-orange-50 border border-orange-300/50 rounded-full text-orange-700 text-base font-semibold mb-8 shadow-lg">
              <Clock className="w-5 h-5 mr-2 text-orange-600" />
              {t('landing.hero.tagline', 'Votre stratégie en moins de 5 minutes')}
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              {t('landing.hero.title', 'Transformez vos ')}
              <span className="bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                {t('landing.hero.titleHighlight1', 'objectifs')}
              </span>
              {t('landing.hero.titleContinue', ' en ')}
              <span className="bg-gradient-to-r from-blue-700 to-blue-900 bg-clip-text text-transparent">
                {t('landing.hero.titleHighlight2', 'plan stratégique')}
              </span>
              {t('landing.hero.titleEnd', ' avec Aladinn')}
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-slate-600 mb-8 lg:mb-12 max-w-4xl mx-auto leading-relaxed px-4">
              La plateforme qui <strong className="font-semibold text-slate-700">structure votre stratégie</strong>, génère votre <strong className="font-semibold text-slate-700">roadmap</strong> ET <strong className="font-semibold text-slate-700">track votre exécution</strong> : un système complet de A à Z.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 lg:mb-10 px-4">
              <button 
                onClick={() => handleAuthWithCleanup('register')}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-6 lg:px-8 py-3 lg:py-4 rounded-full hover:from-blue-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl font-semibold text-base lg:text-lg flex items-center justify-center"
              >
                <Rocket className="w-5 lg:w-6 h-5 lg:h-6 mr-2" />
                {t('landing.hero.ctaPrimary', 'Accéder à ma stratégie personnalisée')}
              </button>
              <button 
                onClick={scrollToDemo}
                className="w-full sm:w-auto text-white font-semibold text-base lg:text-lg flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-gradient-to-r from-orange-500 to-purple-600 rounded-full hover:from-orange-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                <Play className="w-5 lg:w-6 h-5 lg:h-6 mr-2" />
                {t('landing.hero.ctaSecondary', 'Voir la démo')}
              </button>
            </div>

            {/* Preuves sociales */}
            <div className="flex items-center justify-center gap-6 mb-8 text-sm text-slate-600">
              <div className="flex items-center">
                <Users className="w-4 h-4 text-blue-500 mr-1" />
                {t('landing.hero.socialProof.strategiesCreated', '2,847 stratégies créées ce mois')}
              </div>
              <div className="flex items-center">
                <Star className="w-4 h-4 text-yellow-500 mr-1" />
                {t('landing.hero.socialProof.averageRating', 'Moyenne 4.8/5 étoiles')}
              </div>
              <div className="flex items-center">
                <Shield className="w-4 h-4 text-green-500 mr-1" />
                {t('landing.hero.socialProof.freeTrial', '0€ pendant 30 jours')}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8 max-w-4xl mx-auto px-4">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-4 lg:p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200">
                  <div className="text-2xl lg:text-3xl xl:text-4xl font-bold text-blue-600 mb-1 lg:mb-2">{stat.number}</div>
                  <div className="text-slate-700 font-medium text-sm lg:text-base">{stat.label}</div>
                  <div className="text-xs lg:text-sm text-slate-500">{stat.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-3 py-1.5 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-4">
              <Users className="w-4 h-4 mr-2" />
              {t('landing.testimonials.badge', 'Témoignages')}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {t('landing.testimonials.sectionTitle', 'Ce que disent nos utilisateurs')}
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              {t('landing.testimonials.sectionSubtitle', 'Découvrez comment Aladinn transforme la façon de gérer les projets')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 lg:p-8 border border-slate-200 shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className={`${isRTL ? 'mr-4' : 'ml-4'} flex-1`}>
                    <div className="flex text-yellow-400 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className={`text-slate-700 text-base lg:text-lg italic mb-4 leading-relaxed ${textAlign}`}>
                  "{t(`landing.testimonials.testimonial${num}.quote`, '')}"
                </p>
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={textAlign}>
                    <p className="font-semibold text-slate-800">
                      {t(`landing.testimonials.testimonial${num}.author`, '')}
                    </p>
                    <p className="text-sm text-slate-500">
                      {t(`landing.testimonials.testimonial${num}.role`, '')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Aladinn Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-3 py-1.5 bg-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('landing.whyAladinn.badge', 'Pourquoi Aladinn ?')}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {t('landing.whyAladinn.sectionTitle', 'Les 3 Piliers d\'un Projet Réussi')}
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              {t('landing.whyAladinn.sectionSubtitle', 'Une méthodologie complète pour transformer vos idées en réalisations concrètes')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[1, 2, 3].map((num) => (
              <div key={num} className="bg-white rounded-2xl p-6 lg:p-8 border-2 border-slate-200 hover:border-blue-300 shadow-lg hover:shadow-xl transition-all">
                <div className="text-5xl mb-4">
                  {t(`landing.whyAladinn.pillar${num}.icon`, '')}
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-slate-800 mb-4">
                  {t(`landing.whyAladinn.pillar${num}.title`, '')}
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                        <p className="text-xs font-semibold text-red-700 mb-1">
                          {t('landing.whyAladinn.problemLabel', 'Problème courant')}
                        </p>
                        <p className={`text-sm text-red-600 ${textAlign}`}>
                          {t(`landing.whyAladinn.pillar${num}.problem`, '')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                        <p className="text-xs font-semibold text-emerald-700 mb-1">
                          {t('landing.whyAladinn.solutionLabel', 'Solution Aladinn')}
                        </p>
                        <p className={`text-sm text-emerald-600 ${textAlign}`}>
                          {t(`landing.whyAladinn.pillar${num}.solution`, '')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-3 py-1.5 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4">
              <Compass className="w-4 h-4 mr-2" />
              {t('landing.howItWorks.badge', 'Comment ça marche ?')}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {t('landing.howItWorks.sectionTitle', 'Votre projet en 3 étapes simples')}
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              {t('landing.howItWorks.sectionSubtitle', 'Un processus éprouvé pour passer de l\'idée à l\'action')}
            </p>
          </div>

          <div className="relative">
            {/* Timeline connector - hidden on mobile */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-emerald-200" style={{ top: '6rem' }}></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 relative">
              {[
                {
                  title: t('landing.howItWorks.step1.title', 'Structuration'),
                  duration: t('landing.howItWorks.step1.duration', '12 min en moyenne'),
                  items: [
                    t('landing.howItWorks.step1.items.0', 'Analyse de votre objectif'),
                    t('landing.howItWorks.step1.items.1', 'Génération de roadmap visuelle'),
                    t('landing.howItWorks.step1.items.2', 'Identification des ressources clés')
                  ]
                },
                {
                  title: t('landing.howItWorks.step2.title', 'Planification'),
                  duration: t('landing.howItWorks.step2.duration', 'Automatique'),
                  items: [
                    t('landing.howItWorks.step2.items.0', 'Découpage en phases actionnables'),
                    t('landing.howItWorks.step2.items.1', 'Estimation réaliste des délais'),
                    t('landing.howItWorks.step2.items.2', 'Priorisation intelligente')
                  ]
                },
                {
                  title: t('landing.howItWorks.step3.title', 'Exécution'),
                  duration: t('landing.howItWorks.step3.duration', 'Suivi continu'),
                  items: [
                    t('landing.howItWorks.step3.items.0', 'Tracking de progression'),
                    t('landing.howItWorks.step3.items.1', 'Ajustements automatiques'),
                    t('landing.howItWorks.step3.items.2', 'Notifications de rappel')
                  ]
                }
              ].map((step, num) => (
                <div key={num} className="relative">
                  {/* Step number circle */}
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg relative z-10">
                      {num + 1}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 lg:p-8 border-2 border-slate-200 shadow-lg hover:shadow-xl transition-all">
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-800 mb-2 text-center">
                      {step.title}
                    </h3>
                    <div className="flex items-center justify-center mb-4">
                      <Clock className="w-4 h-4 text-blue-600 mr-2" />
                      <p className="text-sm font-semibold text-blue-600">
                        {step.duration}
                      </p>
                    </div>

                    <ul className="space-y-3">
                      {step.items.map((item, idx) => (
                        <li key={idx} className={`flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className={`text-slate-700 ${isRTL ? 'mr-3' : 'ml-3'} ${textAlign}`}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA after How It Works */}
          <div className="text-center mt-12">
            <button 
              onClick={() => handleAuthWithCleanup('register')}
              className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-8 py-4 rounded-full hover:from-blue-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl font-semibold text-lg inline-flex items-center"
            >
              <Rocket className="w-6 h-6 mr-2" />
              {t('landing.hero.ctaPrimary', 'Accéder à ma stratégie personnalisée')}
            </button>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section ref={demoSectionRef} className="py-12 lg:py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 pt-4">
            <div className="inline-flex items-center px-3 py-1.5 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              {t('landing.demo.badge', 'Démonstration gratuite')}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                {t('landing.demo.title', 'Découvrez la puissance d\'Aladinn')}
              </span>
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('landing.demo.subtitle', 'Testez notre intelligence artificielle en définissant votre objectif')}
            </p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-emerald-400/10 to-purple-400/10 rounded-3xl blur-3xl"></div>
            <div className="relative">
              <DemoModule onShowAuth={handleAuthWithCleanup} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl lg:text-2xl font-bold">Aladinn.AI</h1>
            </div>
            <p className="text-slate-400 mb-6 max-w-md mx-auto text-sm lg:text-base">
              {t('landing.footer.description', 'La plateforme éthique qui vous accompagne dans la réalisation de vos ambitions')}
            </p>
            <div className="flex justify-center space-x-6 text-sm text-slate-400">
              <button className="hover:text-white transition-colors">
                {t('landing.footer.legal.0', 'Mentions légales')}
              </button>
              <button className="hover:text-white transition-colors">
                {t('landing.footer.legal.1', 'Confidentialité')}
              </button>
              <button className="hover:text-white transition-colors">
                {t('landing.footer.legal.2', 'CGU')}
              </button>
            </div>
            <p className="text-slate-500 text-sm mt-6">
              {t('landing.footer.copyright', '© 2024 Aladinn.AI. Tous droits réservés.')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
 
export default PublicLandingPage;
